'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MAX_QUANTITY_PER_PRODUCT, getProductById, isPurchasable, type Product } from '@/lib/products';

const STORAGE_KEY = 'tcgsecret-cart';

export interface CartLine {
  product: Product;
  quantity: number;
}

interface CartContextValue {
  lines: CartLine[];
  itemCount: number;
  subtotal: number;
  hasPreorder: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  addItem: (productId: string, quantity?: number) => void;
  setQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

type StoredCart = Record<string, number>;

function clampQuantity(quantity: number) {
  return Math.max(0, Math.min(MAX_QUANTITY_PER_PRODUCT, Math.floor(quantity)));
}

function readStoredCart(): StoredCart {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object') return {};
    const cart: StoredCart = {};
    for (const [id, qty] of Object.entries(parsed)) {
      const product = getProductById(id);
      // Drop products that were removed or sold out since they were added.
      if (product && isPurchasable(product) && typeof qty === 'number') {
        const quantity = clampQuantity(qty);
        if (quantity > 0) cart[id] = quantity;
      }
    }
    return cart;
  } catch {
    return {};
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<StoredCart>({});
  const [loaded, setLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setCart(readStoredCart());
    setLoaded(true);
    // Keep tabs in sync.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setCart(readStoredCart());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {
      // Storage unavailable (private mode) — the cart still works for this visit.
    }
  }, [cart, loaded]);

  const setQuantity = useCallback((productId: string, quantity: number) => {
    setCart((prev) => {
      const next = { ...prev };
      const clamped = clampQuantity(quantity);
      if (clamped > 0) next[productId] = clamped;
      else delete next[productId];
      return next;
    });
  }, []);

  const addItem = useCallback((productId: string, quantity = 1) => {
    const product = getProductById(productId);
    if (!product || !isPurchasable(product)) return;
    setCart((prev) => ({ ...prev, [productId]: clampQuantity((prev[productId] ?? 0) + quantity) }));
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((productId: string) => setQuantity(productId, 0), [setQuantity]);
  const clear = useCallback(() => setCart({}), []);

  const value = useMemo<CartContextValue>(() => {
    const lines = Object.entries(cart).flatMap(([id, quantity]) => {
      const product = getProductById(id);
      return product ? [{ product, quantity }] : [];
    });
    return {
      lines,
      itemCount: lines.reduce((sum, l) => sum + l.quantity, 0),
      subtotal: lines.reduce((sum, l) => sum + l.product.price * l.quantity, 0),
      hasPreorder: lines.some((l) => l.product.preorder),
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      addItem,
      setQuantity,
      removeItem,
      clear,
    };
  }, [cart, drawerOpen, addItem, setQuantity, removeItem, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart must be used inside CartProvider');
  return value;
}
